import { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col } from 'components/atoms/Grid';
import styled from 'styled-components';
import Brand from 'components/atoms/Brand';
import Text from 'components/atoms/Text';
import Icon from '../../atoms/Icon';
import Colors from 'components/tokens/Colors';
import Menu from '../Menu';
import { useEditor } from 'providers/generalContext';
import RouterLink from 'components/atoms/RouterLink';
import { UserContext } from 'contexts';

function Header({ light, title, link = '/' }) {
    const [visible, setVisible] = useState(false);
    const [isVisibleUserMenu, setIsVisibleUserMenu] = useState(false);
    const { isStoryblokEditor } = useEditor();
    const { token, user } = useContext(UserContext);
    const navigate = useNavigate();

    const handleCloseUserMenu = useCallback(() => {
        let element = document.getElementById('root');
        setIsVisibleUserMenu(false);
        element.removeEventListener('click', handleCloseUserMenu);
    }, []);

    useEffect(() => {
        let element = document.getElementById('root');
        if (isVisibleUserMenu) {
            element.addEventListener('click', handleCloseUserMenu);
        }
    }, [isVisibleUserMenu, handleCloseUserMenu]);

    return (
        <>
            <HeaderStyle
                style={{ backgroundColor: light ? '#fff' : '#000' }}
                onClick={visible ? () => setVisible(false) : null}
            >
                <Container px="1.5 4">
                    <Row alignItems="center">
                        {title && !visible ? (
                            <>
                                <Col text="center left" order="1 0" grow="1 0">
                                    <BrandBox light={light} />
                                </Col>
                                <Col order="0 1" grow="0 1">
                                    <Icons
                                        light={light}
                                        visible={visible}
                                        setVisible={setVisible}
                                    />
                                </Col>

                                <Col text="center" size="hidden 6" order="2">
                                    <RouterLink to={link} editor={isStoryblokEditor} decoration="none">
                                        <Text heading5 upper color={light ? Colors.dark : Colors.white}>
                                            {title}
                                        </Text>
                                    </RouterLink>
                                </Col>
                            </>
                        ) : (
                            <>
                                <Col size="auto 3" grow="0">
                                    <Icons
                                        light={light}
                                        visible={visible}
                                        setVisible={setVisible}
                                    />
                                </Col>
                                <Col text="center" grow="1">
                                    <BrandBox light={light} />
                                </Col>
                            </>
                        )}

                        <Col size="hidden 3" order="2" grow="0" text="right">
                            <MenuMember
                                token={token}
                                light={light}
                                user={user}
                                visible={isVisibleUserMenu}
                                onClick={() => setIsVisibleUserMenu(!isVisibleUserMenu)}
                            />
                        </Col>

                        <Col className="icon" grow="0" size="auto hidden" order="1">
                            <Icon
                                name="Busca"
                                color={
                                    light
                                        ? Colors.dark
                                        : window.location.pathname.includes('/busca')
                                        ? Colors.primary
                                        : Colors.white
                                }
                                onClick={() => navigate('/busca')}
                            />
                        </Col>
                    </Row>
                </Container>
            </HeaderStyle>
            <Menu visible={visible} onSetVisible={setVisible} />
        </>
    );
}

const Icons = ({ light, visible, setVisible }) => {
    const navigate = useNavigate();
    return (
        <Row>
            <Col className="icon" grow="0">
                <Icon
                    name={'Menu'}
                    color={visible ? Colors.primary : light ? Colors.dark : Colors.white}
                    onClick={() => setVisible(!visible)}
                />
            </Col>
            <Col className="icon" grow="0" size="hidden auto">
                <Icon
                    name="Busca"
                    color={
                        light
                            ? Colors.dark
                            : window.location.pathname.startsWith('/busca')
                            ? Colors.primary
                            : Colors.white
                    }
                    onClick={() => navigate('/busca')}
                />
            </Col>
        </Row>
    );
};

const MenuMember = ({ light, token, user, visible, ...props }) => {
    const navigate = useNavigate();

    return (
        <div className="menu-member" style={{ color: light ? Colors.dark : Colors.white }} {...props}>
            {token?.length ? (
                <>
                    {user?.active_plan === 'basic' && user?.payment_method !== 'pix' && (
                        <StyledAnchor className="sign" onClick={() => navigate('/contexto/planos')}>
                            <Text body2 highlight style={{ marginRight: '100px' }}>
                                Assine
                            </Text>
                        </StyledAnchor>
                    )}
                    <UserMenuContainer titleLength={user?.name?.split(' ')?.[0]?.length + 3}>
                        <UserMenu visible={visible}>
                            <span className="icon" style={{ lineHeight: 0 }}>
                                <Icon
                                    name="People"
                                    color={visible ? Colors.dark : light ? Colors.dark : Colors.white}
                                />
                            </span>
                            <Text body>{user?.name?.split(' ')[0]}</Text>
                        </UserMenu>
                        <UserMenuDropDown visible={visible}>
                            <StyledItem onClick={() => navigate('/contexto')}>
                                <Text body3>Página inicial</Text>
                            </StyledItem>
                            <StyledItem
                                onClick={() => navigate('/contexto/configuracoes', { state: { selected: 'editUser' } })}
                            >
                                <Text body3>Conta</Text>
                            </StyledItem>
                            {user?.payment_method === 'credit_card' || user?.payment_method === 'pix' ? (
                                <StyledItem
                                    onClick={() =>
                                        navigate('/contexto/configuracoes', { state: { selected: 'editPlan' } })
                                    }
                                >
                                    <Text body3>Assinatura</Text>
                                </StyledItem>
                            ) : null}
                            <StyledItem
                                onClick={() => navigate('/contexto/configuracoes', { state: { selected: 'faq' } })}
                            >
                                <Text body3>Perguntas frequentes</Text>
                            </StyledItem>
                            <StyledItem
                                onClick={e => {
                                    e.preventDefault();
                                    navigate('/login', { state: { logout: true } });
                                }}
                            >
                                <Text body3 color={Colors.grey}>
                                    Sair
                                </Text>
                            </StyledItem>
                        </UserMenuDropDown>
                    </UserMenuContainer>
                </>
            ) : (
                <>
                    <StyledAnchor onClick={() => navigate('/contexto')}>
                        <Text body2 highlight>
                            Seja membro
                        </Text>
                    </StyledAnchor>
                    <StyledAnchor onClick={() => navigate('/login')} pl={26}>
                        <span className="icon">
                            <Icon name="People" color={light ? Colors.dark : Colors.white} />
                        </span>
                        <Text body2>Entrar</Text>
                    </StyledAnchor>
                </>
            )}
        </div>
    );
};

const StyledItem = styled.div`
    cursor: pointer;
    padding: 4px 8px;
    background-color: ${Colors.white};
    transition: background-color 0.2s;

    &:first-child {
        padding-top: 8px;
    }

    &:last-child {
        padding-bottom: 8px;
        border-radius: 0 0 4px 4px;
    }

    &:hover {
        background-color: ${Colors.lightGrey};
    }
`;

const BrandBox = ({ light }) => (
    <a href="/">
        <Brand light={!light} />
    </a>
);

const StyledAnchor = styled.div`
    text-decoration: none;
    color: inherit;
    display: flex;
    align-items: center;
    white-space: nowrap;
    padding-left: ${({ pl }) => pl ?? '0'}px;

    span {
        cursor: pointer;
    }

    &.sign:hover {
        color: ${Colors.primary};
        transition: all 0.2s ease;
    }
`;

const HeaderStyle = styled.header`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 80px;

    .icon {
        cursor: pointer;
        height: 100%;
        line-height: 0;
        padding-top: 12px;
        padding-bottom: 12px;
        padding-right: 12px;
    }

    .menu-member {
        display: flex;
        align-items: center;
        justify-content: flex-end;
    }

    @media (max-width: 768px) {
        position: sticky;
        top: 0;
        z-index: 999;
    }
`;

const UserMenuContainer = styled.div`
    width: ${({ titleLength }) => `${titleLength}ch`};
    min-width: 90px;
    position: absolute;
    right: 0%;
    top: -7px;
    z-index: 2;
`;

const UserMenu = styled.div`
    transition: all 200ms;
    display: flex;
    align-items: center;
    border-radius: ${({ visible }) => (visible ? '8px 8px 0 0' : '8px')};
    padding: 4px 4.5px;
    background-color: ${({ visible }) => (visible ? Colors.primary : Colors.darkGrey)};
    color: ${({ visible }) => (visible ? Colors.black : Colors.white)};

    &:hover {
        cursor: pointer;
    }

    span.icon {
        line-height: 0;
        height: 100%;
        padding-top: 0;
        padding-bottom: 0;
        padding-right: 8px;
    }
`;

const UserMenuDropDown = styled.div`
    transition: height 200ms;
    height: ${({ visible }) => (visible ? 'auto' : '0')};
    display: ${({ visible }) => (visible ? 'flex' : 'none')};
    flex-direction: column;
    background-color: ${Colors.white};
    color: ${Colors.black};
    border-radius: 0 0 8px 8px;
    text-align: left;
`;

export default Header;
